import { mapValues } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { AnalysisSet } from '@paragon/contract-library';
import { keyMapBy } from '@paragon/utilities';

import { RootState } from '..';

export const getAnalysisSetsMap = createSelector(
  (state: RootState) => state.analysisSets.sets,
  (analysisSets) => keyMapBy(analysisSets, (analysisSet) => analysisSet.guid),
);

/**
 * Schedule stations do not load the whole analysis set, but in some instances we want to know the component design guid
 * without thinking about what context we are in. This selector merges data from the analysis sets slice with the
 * production models slice to provide that, and throws an error if a conflict is found, which should never happen.
 */
export const getAnalysisSetGuidToComponentDesignGuid = createSelector(
  getAnalysisSetsMap,
  (state: RootState) => state.productionModels.analysisSetGuidToComponentDesignGuid,
  (analysisSetsMap, productionModelsAnalysisSetGuidToComponentDesignGuid) => {
    const analysisSetGuidToComponentDesignGuidMap = mapValues(
      Object.fromEntries(analysisSetsMap),
      (analysisSet) => analysisSet.componentDesign.guid,
    );
    for (const [analysisSetGuid, componentDesignGuid] of Object.entries(
      productionModelsAnalysisSetGuidToComponentDesignGuid,
    )) {
      if (analysisSetGuid in analysisSetGuidToComponentDesignGuidMap) {
        throw new Error(
          'Duplicate analysis set guid found while merging analysis sets slice and production models slice',
        );
      }
      analysisSetGuidToComponentDesignGuidMap[analysisSetGuid] = componentDesignGuid;
    }
    return analysisSetGuidToComponentDesignGuidMap;
  },
);

export function getObjectCheckFailureSummaryString(guid: string, analysisSet: AnalysisSet | null) {
  const objectCheckFailureSummary = analysisSet?.objectCheckFailureSummaries?.find(
    (objectCheckFailureSummary) => objectCheckFailureSummary.objectChecked.guid === guid,
  );
  return objectCheckFailureSummary == null
    ? ''
    : `${Math.round(objectCheckFailureSummary.capacityRatio * 100)}% ` +
        `(${objectCheckFailureSummary.checkFailureSummary})`;
}
